import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';
import ProductEditModal from '../../components/feature/ProductEditModal';
import { getProductsByCategory, Product, categories } from '../../mocks/products';
import { useInquiry } from '../../contexts/InquiryContext';
import { useAuth } from '../../contexts/AuthContext';
import { useCategoryContext } from '../../contexts/CategoryContext';

// 관련 제품 카드 컴포넌트
function RelatedProductCard({ product }: { product: Product }) {
  // 용량 표시 로직: volumeOptions가 있으면 정확하게 나열, 없으면 단일 용량 표시
  const volumeDisplay = product.volumeOptions && product.volumeOptions.length > 1
    ? product.volumeOptions.join(', ')
    : product.volume;

  return (
    <Link
      to={`/products/${product.id}`}
      className="group cursor-pointer"
    >
      <div className="w-full aspect-square rounded-lg overflow-hidden bg-white flex items-center justify-center mb-4">
        <img
          src={product.thumbnail}
          alt={product.name}
          className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <p className="text-sm text-emerald-600 mb-1">{product.nameEn}</p>
      <h3 className="text-base font-medium text-gray-800 mb-2">
        {product.name}
        <span className="text-sm font-normal text-gray-500 ml-2 md:ml-3">{volumeDisplay}</span>
      </h3>
      <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">{product.recommendation}</p>
    </Link>
  );
}

// 특징별 아이콘 매핑 함수
function getFeatureIcon(feature: string, index: number): string {
  const lowerFeature = feature.toLowerCase();
  
  if (lowerFeature.includes('발효') || lowerFeature.includes('흡수')) {
    return 'ri-drop-line';
  }
  if (lowerFeature.includes('병해') || lowerFeature.includes('방제') || lowerFeature.includes('예방') || lowerFeature.includes('사멸')) {
    return 'ri-shield-check-line';
  }
  if (lowerFeature.includes('침투') || lowerFeature.includes('이행')) {
    return 'ri-arrow-right-down-line';
  }
  if (lowerFeature.includes('안전') || lowerFeature.includes('안정')) {
    return 'ri-check-double-line';
  }
  if (lowerFeature.includes('뿌리') || lowerFeature.includes('발육') || lowerFeature.includes('생육')) {
    return 'ri-seedling-line';
  }
  if (lowerFeature.includes('스트레스') || lowerFeature.includes('고온') || lowerFeature.includes('저온')) {
    return 'ri-temp-hot-line';
  }
  if (lowerFeature.includes('영양') || lowerFeature.includes('성분')) {
    return 'ri-flask-line';
  }
  if (lowerFeature.includes('토양') || lowerFeature.includes('미생물')) {
    return 'ri-plant-line';
  }
  if (lowerFeature.includes('혼용') || lowerFeature.includes('경제')) {
    return 'ri-money-dollar-circle-line';
  }
  if (lowerFeature.includes('규산') || lowerFeature.includes('조직') || lowerFeature.includes('강화')) {
    return 'ri-shield-line';
  }
  
  // 기본 아이콘 (순서대로 다른 아이콘 사용)
  const defaultIcons = ['ri-checkbox-circle-line', 'ri-star-line', 'ri-award-line', 'ri-thumb-up-line', 'ri-heart-line', 'ri-lightbulb-line'];
  return defaultIcons[index % defaultIcons.length];
}

// 파일이 이미지인지 확인
const isImageFile = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ['jpg', 'jpeg', 'png', 'gif'].includes(ext);
};

const getFileIcon = (fileName: string) => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return 'ri-image-line';
  if (ext === 'pdf') return 'ri-file-pdf-2-line';
  if (['doc', 'docx'].includes(ext)) return 'ri-file-word-line';
  if (['xls', 'xlsx'].includes(ext)) return 'ri-file-excel-line';
  if (['ppt', 'pptx'].includes(ext)) return 'ri-file-ppt-line';
  if (ext === 'zip') return 'ri-file-zip-line';
  return 'ri-file-line';
};

// 제품별 문의 리스트 컴포넌트
function ProductInquiryList({ productId, product }: { productId: string; product: Product }) {
  const { inquiries, deleteInquiry, updateInquiryReply } = useInquiry();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState<any>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);

  const ITEMS_PER_PAGE = 10;

  // 현재 제품에 해당하는 문의만 필터링 (product_info에 제품명이 포함된 경우)
  const productInquiries = inquiries.filter(inquiry => {
    if (!inquiry.product_info) return false;
    const productInfo = inquiry.product_info.toLowerCase();
    return productInfo.includes(product.name.toLowerCase()) || 
           productInfo.includes(product.nameEn.toLowerCase());
  });

  const filteredInquiries = searchKeyword.trim()
    ? productInquiries.filter(
        (inq) =>
          inq.title.toLowerCase().includes(searchKeyword.trim().toLowerCase()) ||
          inq.name.toLowerCase().includes(searchKeyword.trim().toLowerCase())
      )
    : productInquiries;
  
  const totalPages = Math.ceil(filteredInquiries.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentInquiries = filteredInquiries.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleInquiryClick = (inquiry: any) => {
    if (isAdmin) {
      setSelectedInquiry(inquiry);
      setReplyText(inquiry.reply_content || '');
      setShowReplyForm(false);
      setShowContentModal(true);
      return;
    }
    
    setSelectedInquiry(inquiry);
    setShowPasswordModal(true);
    setPasswordInput('');
    setPasswordError(false);
  };

  const handlePasswordSubmit = () => {
    if (selectedInquiry && passwordInput === selectedInquiry.password) {
      setShowPasswordModal(false);
      setReplyText(selectedInquiry.reply_content || '');
      setShowReplyForm(false);
      setShowContentModal(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
  };

  const closeModals = () => {
    setShowPasswordModal(false);
    setShowContentModal(false);
    setShowDeleteConfirm(false);
    setSelectedInquiry(null);
    setPasswordInput('');
    setPasswordError(false);
    setReplyText('');
    setShowReplyForm(false);
  };

  const handleDelete = async () => {
    if (selectedInquiry) {
      try {
        await deleteInquiry(selectedInquiry.id);
        closeModals();
        alert('문의가 삭제되었습니다.');
        sessionStorage.setItem('scrollToProductInquiry', 'true');
        window.location.reload();
      } catch (error) {
        alert('삭제 중 오류가 발생했습니다.');
      }
    }
  };

  const handleInquiryButtonClick = () => {
    sessionStorage.setItem('scrollToProductInquiry', 'true');
    navigate('/inquiry', {
      state: {
        productId: product.id,
        productNameEn: product.nameEn,
        productName: product.name,
        productVolume: product.volumeOptions && product.volumeOptions.length > 1
          ? product.volumeOptions.join(', ')
          : product.volume
      }
    });
  };

  const handleReplySubmit = async () => {
    if (!selectedInquiry || !replyText.trim()) return;
    try {
      setReplySubmitting(true);
      await updateInquiryReply(selectedInquiry.id, replyText.trim());
      setSelectedInquiry({ ...selectedInquiry, reply_content: replyText.trim(), reply_date: new Date().toISOString(), status: '답변완료' });
      setShowReplyForm(false);
    } catch (error) {
      console.error('답변 등록 오류:', error);
      alert('답변 등록 중 오류가 발생했습니다.');
    } finally {
      setReplySubmitting(false);
    }
  };

  return (
    <>
      {/* 문의하기 버튼 - 게시판 우측 상단 */}
      <div className="flex justify-end mb-4 md:mb-6">
        <button
          onClick={handleInquiryButtonClick}
          className="bg-primary text-white px-4 md:px-6 py-2 md:py-2.5 rounded-full text-sm md:text-base font-medium hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-edit-line text-base md:text-lg"></i>
          문의 작성
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* PC 테이블 헤더 */}
        <div className="hidden md:block bg-gray-100 px-6 py-3">
          <div className="grid grid-cols-12 gap-4 text-[#222222] font-semibold text-sm">
            <div className="col-span-1 text-center">번호</div>
            <div className="col-span-5">제목</div>
            <div className="col-span-2 text-center">작성자</div>
            <div className="col-span-2 text-center">작성일</div>
            <div className="col-span-2 text-center">상태</div>
          </div>
        </div>
        {/* 모바일 테이블 헤더 */}
        <div className="block md:hidden bg-gray-100 px-4 py-3">
          <div className="grid grid-cols-12 gap-2 text-[#222222] font-semibold text-xs">
            <div className="col-span-1 text-center">No</div>
            <div className="col-span-5">제목</div>
            <div className="col-span-2 text-center">작성자</div>
            <div className="col-span-2 text-center">작성일</div>
            <div className="col-span-2 text-center">상태</div>
          </div>
        </div>
        <div>
          {currentInquiries.length === 0 ? (
            <div className="px-4 md:px-6 py-12 md:py-16 text-center">
              <i className="ri-file-list-3-line text-3xl md:text-4xl text-gray-300 mb-3 block"></i>
              <p className="text-sm md:text-base text-gray-400">
                {searchKeyword.trim() ? '검색 결과가 없습니다.' : '이 제품에 대한 문의가 아직 없습니다.'}
              </p>
            </div>
          ) : (
            currentInquiries.map((inquiry, index) => (
              <div
                key={inquiry.id}
                onClick={() => handleInquiryClick(inquiry)}
                className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-100 hover:bg-emerald-50/30 transition-colors cursor-pointer"
              >
                {/* PC 레이아웃 */}
                <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-1 text-center text-[#222222] text-sm">{startIndex + index + 1}</div>
                  <div className="col-span-5 text-[#222222] font-medium text-sm flex items-center gap-2">
                    <i className="ri-lock-line text-sm text-gray-400"></i>
                    {inquiry.title}
                    {inquiry.attachment_url && (
                      <i className="ri-attachment-2 text-sm text-gray-400" title="첨부파일"></i>
                    )}
                  </div>
                  <div className="col-span-2 text-center text-[#222222] text-sm">{inquiry.name}</div>
                  <div className="col-span-2 text-center text-[#222222] text-sm">
                    {new Date(inquiry.created_at).toLocaleDateString('ko-KR')}
                  </div>
                  <div className="col-span-2 text-center">
                    <span className={`text-sm font-medium ${inquiry.status === '답변완료' ? 'text-emerald-600' : 'text-[#222222]'}`}>
                      {inquiry.status === '답변완료' ? '답변완료' : '답변대기'}
                    </span>
                  </div>
                </div>
                {/* 모바일 레이아웃 */}
                <div className="grid md:hidden grid-cols-12 gap-2 items-center">
                  <div className="col-span-1 text-center text-[#222222] text-xs">{startIndex + index + 1}</div>
                  <div className="col-span-5 text-[#222222] font-medium text-xs flex items-center gap-1 truncate">
                    <i className="ri-lock-line text-xs text-gray-400 shrink-0"></i>
                    <span className="truncate">{inquiry.title}</span>
                    {inquiry.attachment_url && (
                      <i className="ri-attachment-2 text-xs text-gray-400 shrink-0"></i>
                    )}
                  </div>
                  <div className="col-span-2 text-center text-[#222222] text-xs truncate">{inquiry.name}</div>
                  <div className="col-span-2 text-center text-[#222222] text-xs">
                    {new Date(inquiry.created_at).toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' })}
                  </div>
                  <div className="col-span-2 text-center">
                    <span className={`text-xs font-medium ${inquiry.status === '답변완료' ? 'text-emerald-600' : 'text-[#222222]'}`}>
                      {inquiry.status === '답변완료' ? '완료' : '대기'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-8">
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-sm transition-colors cursor-pointer ${
                currentPage === 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-skip-back-mini-line text-base md:text-lg"></i>
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-sm transition-colors cursor-pointer ${
                currentPage === 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-arrow-left-s-line text-base md:text-lg"></i>
            </button>
            
            {getPageNumbers().map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-xs md:text-sm font-medium transition-colors cursor-pointer ${
                  currentPage === page
                    ? 'bg-emerald-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {page}
              </button>
            ))}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-sm transition-colors cursor-pointer ${
                currentPage === totalPages
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-arrow-right-s-line text-base md:text-lg"></i>
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className={`w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded text-sm transition-colors cursor-pointer ${
                currentPage === totalPages
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-skip-forward-mini-line text-base md:text-lg"></i>
            </button>
          </div>
        </div>
      )}

      {/* 검색 영역 - 게시판 하단 */}
      <div className="flex justify-center mt-2.5 md:mt-4">
        <div className="w-full md:w-1/2 flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden">
          <input
            type="text"
            placeholder="제목 또는 작성자 검색"
            value={searchKeyword}
            onChange={(e) => {
              setSearchKeyword(e.target.value);
              if (e.target.value === '') setCurrentPage(1);
            }}
            onKeyDown={handleSearchKeyDown}
            className="flex-1 min-w-0 bg-white px-3 md:px-4 py-2.5 text-sm focus:outline-none text-[#222222] placeholder-gray-400"
          />
          <button
            onClick={handleSearch}
            className="px-3 md:px-4 py-2.5 text-gray-500 hover:text-emerald-600 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-search-line text-lg"></i>
          </button>
        </div>
      </div>

      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeModals}>
          <div className="bg-white rounded-lg p-8 w-[400px] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-[#222222]">비밀번호 확인</h3>
              <button onClick={closeModals} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <p className="text-base text-[#222222] mb-5">게시글을 확인하려면 비밀번호를 입력해주세요.</p>
            <input
              type="password"
              placeholder="비밀번호"
              value={passwordInput}
              onChange={(e) => {
                setPasswordInput(e.target.value);
                setPasswordError(false);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              className={`w-full bg-gray-50 rounded-lg px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-emerald-600 mb-2 ${passwordError ? 'ring-2 ring-red-400' : ''}`}
            />
            {passwordError && (
              <p className="text-sm text-red-500 mb-4">비밀번호가 일치하지 않습니다.</p>
            )}
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={closeModals}
                className="px-5 py-2.5 text-base text-gray-600 hover:text-gray-800 cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handlePasswordSubmit}
                className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg text-base font-medium hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {showContentModal && selectedInquiry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeModals}>
          <div className="bg-white rounded-lg p-8 w-[540px] max-h-[80vh] overflow-y-auto shadow-xl inquiry-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-[#222222]">{selectedInquiry.title || '문의 내용'}</h3>
              <button onClick={closeModals} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            {selectedInquiry.product_info && (
              <div className="bg-emerald-50 rounded-lg p-4 mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-product-hunt-line text-emerald-600 text-lg"></i>
                  <span className="text-sm font-semibold text-emerald-700">문의 제품</span>
                </div>
                <p className="text-base text-gray-800 font-medium">{selectedInquiry.product_info}</p>
              </div>
            )}

            <div className="border-b border-gray-200 pb-5 mb-5">
              <div className="flex items-center gap-5 text-sm text-gray-500 mb-3">
                <span>작성자: {selectedInquiry.name}</span>
                <span>작성일: {new Date(selectedInquiry.created_at).toLocaleDateString('ko-KR')}</span>
              </div>
              {selectedInquiry.email && (
                <p className="text-sm text-gray-600 mb-1">이메일: {selectedInquiry.email}</p>
              )}
              {selectedInquiry.phone && (
                <p className="text-sm text-gray-600 mb-1">연락처: {selectedInquiry.phone}</p>
              )}
              {selectedInquiry.company && (
                <p className="text-sm text-gray-600">회사명: {selectedInquiry.company}</p>
              )}
            </div>
            <div className="bg-gray-50 rounded-lg p-5 mb-5">
              <p className="text-base text-[#222222] leading-relaxed whitespace-pre-wrap">
                {selectedInquiry.message}
              </p>
            </div>

            {/* 첨부파일 표시 */}
            {selectedInquiry.attachment_url && selectedInquiry.attachment_name && (
              <div className="border border-gray-200 rounded-lg p-4 mb-5">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-attachment-2 text-gray-500 text-base"></i>
                  <span className="text-sm font-semibold text-[#222222]">첨부파일</span>
                </div>
                {isImageFile(selectedInquiry.attachment_name) && (
                  <div
                    className="mb-3 rounded-lg overflow-hidden bg-gray-50 cursor-pointer"
                    onClick={() => setShowImagePreview(true)}
                  >
                    <img
                      src={selectedInquiry.attachment_url}
                      alt={selectedInquiry.attachment_name}
                      className="w-full max-h-[300px] object-contain"
                    />
                  </div>
                )}
                <a
                  href={selectedInquiry.attachment_url}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className={`${getFileIcon(selectedInquiry.attachment_name)} text-xl text-emerald-600`}></i>
                  </div>
                  <span className="text-sm text-emerald-600 font-medium truncate">{selectedInquiry.attachment_name}</span>
                  <i className="ri-download-2-line text-base text-gray-400 shrink-0 ml-auto"></i>
                </a>
              </div>
            )}

            {/* 답변 영역 */}
            <div className="border-t border-gray-200 pt-5 mb-5">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-chat-3-line text-emerald-600 text-lg"></i>
                <span className="text-sm font-bold text-[#222222]">담당자 답변</span>
                {selectedInquiry.status === '답변완료' && (
                  <span className="text-xs bg-emerald-600/10 text-emerald-600 px-2 py-0.5 rounded-full font-medium">답변완료</span>
                )}
              </div>

              {selectedInquiry.reply_content && !showReplyForm ? (
                <div className="bg-emerald-50/50 rounded-lg p-5 mb-3">
                  <p className="text-base text-[#222222] leading-relaxed whitespace-pre-wrap">{selectedInquiry.reply_content}</p>
                  {selectedInquiry.reply_date && (
                    <p className="text-xs text-gray-400 mt-3">
                      답변일: {new Date(selectedInquiry.reply_date).toLocaleDateString('ko-KR')} {new Date(selectedInquiry.reply_date).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => { setShowReplyForm(true); setReplyText(selectedInquiry.reply_content || ''); }}
                      className="mt-3 text-sm text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer flex items-center gap-1"
                    >
                      <i className="ri-edit-2-line text-sm"></i>
                      답변 수정
                    </button>
                  )}
                </div>
              ) : !showReplyForm && !isAdmin ? (
                <div className="bg-gray-50 rounded-lg p-5 text-center">
                  <i className="ri-time-line text-2xl text-gray-300 mb-2 block"></i>
                  <p className="text-sm text-gray-400">아직 답변이 등록되지 않았습니다.</p>
                </div>
              ) : null}

              {/* 관리자 답변 작성/수정 폼 */}
              {isAdmin && (showReplyForm || !selectedInquiry.reply_content) && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    maxLength={500}
                    placeholder="답변 내용을 입력해주세요 (최대 500자)"
                    className="w-full bg-white rounded-lg px-4 py-3 text-sm md:text-base h-28 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-600 mb-2"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{replyText.length}/500</span>
                    <div className="flex gap-2">
                      {showReplyForm && selectedInquiry.reply_content && (
                        <button
                          onClick={() => { setShowReplyForm(false); setReplyText(selectedInquiry.reply_content || ''); }}
                          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer whitespace-nowrap"
                        >
                          취소
                        </button>
                      )}
                      <button
                        onClick={handleReplySubmit}
                        disabled={replySubmitting || !replyText.trim()}
                        className="bg-emerald-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {replySubmitting && <i className="ri-loader-4-line animate-spin text-sm"></i>}
                        {replySubmitting ? '등록 중...' : selectedInquiry.reply_content ? '답변 수정' : '답변 등록'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-between">
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="bg-red-500 text-white px-5 py-2.5 rounded-lg text-base font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-delete-bin-line"></i>
                삭제
              </button>
              <button
                onClick={closeModals}
                className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg text-base font-medium hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-lg p-8 w-[400px] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-[#222222]">삭제 확인</h3>
              <button onClick={() => setShowDeleteConfirm(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            <p className="text-base text-[#222222] mb-6">정말로 이 문의를 삭제하시겠습니까?<br />삭제된 문의는 복구할 수 없습니다.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-5 py-2.5 text-base text-gray-600 hover:text-gray-800 cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-500 text-white px-5 py-2.5 rounded-lg text-base font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {showImagePreview && selectedInquiry?.attachment_url && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] px-4"
          onClick={() => setShowImagePreview(false)}
        >
          <button
            onClick={() => setShowImagePreview(false)}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/20 rounded-full text-white hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <img
            src={selectedInquiry.attachment_url}
            alt={selectedInquiry.attachment_name}
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

export default function ProductDetailPage() {
  const { productId } = useParams();
  const { isAdmin } = useAuth();
  const { getProductById, updateProduct, deleteProduct, dynamicProducts, categoryList } = useCategoryContext();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const navigate = useNavigate();

  // 페이지 진입 시 스크롤 처리
  useEffect(() => {
    const shouldScrollToInquiry = sessionStorage.getItem('scrollToProductInquiry');
    if (shouldScrollToInquiry === 'true') {
      sessionStorage.removeItem('scrollToProductInquiry');
      setTimeout(() => {
        const el = document.getElementById('product-inquiry-section');
        if (el) {
          el.scrollIntoView({ behavior: 'auto' });
        }
      }, 300);
    }
  }, [productId]);

  // 통합 데이터에서 제품 조회
  const product = productId ? getProductById(productId) : undefined;

  const handleSaveProduct = (updatedProduct: Product) => {
    updateProduct(updatedProduct);
    setIsEditModalOpen(false);
    navigate(0);
  };

  const handleDeleteProduct = (id: string) => {
    deleteProduct(id);
    setIsEditModalOpen(false);
    navigate('/products');
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-5">제품을 찾을 수 없습니다</h2>
          <Link to="/products" className="text-emerald-600 hover:underline cursor-pointer text-base">
            제품 목록으로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  // 같은 카테고리의 관련 제품 (현재 제품 제외, 최대 4개)
  const relatedProducts = dynamicProducts
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  // categoryInfo: Context의 categoryList에서 먼저 찾고, 없으면 mock categories에서 fallback
  const categoryFromContext = categoryList.find(c => c.id === product.category);
  const categoryInfo = categoryFromContext
    ? { title: categoryFromContext.title, description: categoryFromContext.description }
    : (categories[product.category] ?? { title: product.category, description: '' });

  // 용량 표시 로직: volumeOptions가 있으면 정확하게 나열, 없으면 단일 용량 표시
  const volumeDisplay = product.volumeOptions && product.volumeOptions.length > 1
    ? product.volumeOptions.join(', ')
    : product.volume;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-0 md:pt-20">
        {/* 상단 제품 정보 섹션 */}
        <section className="py-10 md:py-20">
          <div className="max-w-[1400px] mx-auto px-5">
            {/* 관리자용 수정 버튼 */}
            {isAdmin && (
              <div className="flex justify-end mb-6">
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="bg-emerald-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-edit-line text-lg"></i>
                  상품 수정
                </button>
              </div>
            )}

            <div className="flex flex-col md:flex-row md:gap-16">
              {/* 왼쪽: 제품 썸네일 - 모바일에서 위에 */}
              <div className="flex-shrink-0 mb-6 md:mb-0">
                <div className="w-full md:w-[480px] aspect-square md:h-[480px] flex items-center justify-center">
                  <img
                    src={product.thumbnail}
                    alt={product.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>
              </div>

              {/* 오른쪽: 제품 정보 - 모바일에서 아래에 */}
              <div className="flex-1 flex flex-col justify-center">
                {/* 1. 영문 제품명 */}
                <p className="text-emerald-600 text-sm md:text-base tracking-wide mb-2 md:mb-3">{product.nameEn}</p>
                
                {/* 2. 제품명 + 용량 */}
                <h1 className="text-xl md:text-3xl font-bold text-gray-900 mb-3 md:mb-4">
                  {product.name}
                  <span className="text-base md:text-xl font-normal text-gray-500 ml-2 md:ml-3">{volumeDisplay}</span>
                </h1>
                
                {/* 3. 요약설명 */}
                <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-6 md:mb-8">
                  {product.recommendation}
                </p>

                {/* 4. 구분선 */}
                <div className="border-t border-gray-200 mb-6 md:mb-8"></div>

                {/* 5. 카테고리 */}
                <div className="mb-6 md:mb-8">
                  <h3 className="text-emerald-600 text-sm md:text-base font-medium mb-2">{categoryInfo.title}</h3>
                  <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                    {categoryInfo.description}
                  </p>
                </div>

                {/* 6. 제품 특징 간단설명 */}
                <div>
                  <h3 className="text-emerald-600 text-sm md:text-base font-medium mb-2">제품 특징</h3>
                  <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                    {product.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 구분선 */}
        <div className="max-w-[1400px] mx-auto px-5">
          <div className="border-t border-gray-200"></div>
        </div>

        {/* 하단 상세 설명 섹션 */}
        <section className="py-12 md:py-20 pb-20 md:pb-40">
          <div className="max-w-[1400px] mx-auto px-5">
            {/* 1. 제품 특징 */}
            <div className="mb-12 md:mb-20">
              <h2 className="text-emerald-600 text-sm md:text-base font-medium mb-4 md:mb-6">제품 특징</h2>
              <div className="text-sm md:text-base text-gray-700 leading-loose space-y-3 md:space-y-5">
                {product.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3 md:gap-4">
                    <span className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center text-emerald-600 mt-0.5">
                      <i className={getFeatureIcon(feature, index)}></i>
                    </span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 mb-12 md:mb-20"></div>

            {/* 2. 성분 분석 및 메커니즘 */}
            <div className="mb-12 md:mb-20">
              <h2 className="text-emerald-600 text-sm md:text-base font-medium mb-4 md:mb-6">성분 분석 및 메커니즘</h2>
              <div className="text-sm md:text-base text-gray-700 leading-loose space-y-3 md:space-y-5">
                {(product.mechanism || product.description).split('\\n').filter(line => line.trim()).map((line, index) => (
                  <div key={index} className="flex items-start gap-3 md:gap-4">
                    <span className="text-gray-400 mt-0.5">·</span>
                    <span>{line.trim()}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-100 mb-12 md:mb-20"></div>

            {/* 4. 사용 방법 */}
            <div className="mb-12 md:mb-20">
              <h2 className="text-emerald-600 text-sm md:text-base font-medium mb-5 md:mb-8">사용 방법</h2>
              <ul className="space-y-3 md:space-y-5">
                {(product.usageDetail || [product.dosage]).map((item, index) => (
                  <li key={index} className="flex items-start gap-3 md:gap-5">
                    <span className="text-sm md:text-base text-gray-400 mt-0.5">{String(index + 1).padStart(2, '0')}</span>
                    <span className="text-sm md:text-base text-gray-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-gray-100 mb-12 md:mb-20"></div>

            {/* 5. 사용 시 주의사항 */}
            <div className="mb-16 md:mb-24">
              <h2 className="text-emerald-600 text-sm md:text-base font-medium mb-5 md:mb-8">사용 시 주의사항</h2>
              <ul className="space-y-3 md:space-y-5">
                {(product.caution || ['사용 전 라벨을 꼼꼼히 확인하세요.', '어린이 손이 닿지 않는 곳에 보관하세요.', '직사광선을 피해 서늘한 곳에 보관하세요.']).map((item, index) => (
                  <li key={index} className="flex items-start gap-3 md:gap-5">
                    <span className="text-sm md:text-base text-gray-400 mt-0.5">·</span>
                    <span className="text-sm md:text-base text-gray-700 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-gray-200 mb-12 md:mb-20"></div>

            {/* 관련 제품 */}
            {relatedProducts.length > 0 && (
              <div className="mb-12 md:mb-20">
                <h2 className="text-sm md:text-base text-emerald-600 font-medium mb-6 md:mb-10">함께 구매하면 좋은 제품</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-10">
                  {relatedProducts.map((relatedProduct) => (
                    <RelatedProductCard key={relatedProduct.id} product={relatedProduct} />
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-gray-200 mb-12 md:mb-20"></div>

            {/* 제품 문의 리스트 */}
            <div id="product-inquiry-section">
              <h2 className="text-xl md:text-3xl font-bold text-[#222222] mb-2 md:mb-3">제품 문의</h2>
              <p className="text-sm md:text-base text-[#222222] mb-6 md:mb-10">이 제품에 대한 문의사항을 확인하세요</p>
              <ProductInquiryList productId={productId!} product={product} />
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTopButton />

      {/* 상품 수정 모달 */}
      <ProductEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        product={product}
        onSave={handleSaveProduct}
        onDelete={handleDeleteProduct}
      />
    </div>
  );
}